import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { StoreState } from '../../../redux/Store';
import { hideActiveLayers, removeAllLayers } from '../../../redux/active_layers/Actions';

import { makeStyles, createStyles } from '@material-ui/styles';
import { Typography, IconButton, Menu, MenuItem, Theme } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import LayersIcon from '@material-ui/icons/Layers';

import MapButton from './Button';
import ScrollableContainer from '../../utility/ScrollableContainer';
import Legend from '../../panels/Legend';

function LegendControl(): JSX.Element {
    const classes = useStyles();
    const activeLayers = useSelector((state: StoreState) => state.activeLayers);
    const areSiteCardsVisible = useSelector(
        (state: StoreState) => state.siteCard.areSiteCardsVisible
    );
    const dispatch = useDispatch();
    const dispatchHideAllLayers = () => dispatch(hideActiveLayers());
    const dispatchRemoveAllLayers = () => dispatch(removeAllLayers());
    const [isOpen, setIsOpen] = useState(
        activeLayers.filter(l => l.visible || l.pinned).length > 0
    );
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    function onToggleOpen() {
        setIsOpen(prevIsOpen => !prevIsOpen);
    }

    function handleMenu(e: any) {
        setAnchorEl(e.currentTarget);
    }

    function handleCloseMenu() {
        setAnchorEl(null);
    }

    useEffect(() => {
        const newIsOpen = activeLayers.filter(l => l.visible || l.pinned).length > 0;

        if (newIsOpen && !isOpen) {
            setIsOpen(true);
        }

        if (!newIsOpen && isOpen) {
            setIsOpen(false);
        }
    }, [activeLayers]);

    function handleHideAllLayers() {
        dispatchHideAllLayers();
        handleCloseMenu();
    }

    function handleRemoveAllLayers() {
        dispatchRemoveAllLayers();
        handleCloseMenu();
    }

    return (
        <>
            {(activeLayers.length > 0 || areSiteCardsVisible) && (
                <MapButton onClick={onToggleOpen} dense={false}>
                    <LayersIcon />
                </MapButton>
            )}
            {isOpen || areSiteCardsVisible ? (
                <div className={classes.root}>
                    <div className={classes.header}>
                        <Typography classes={{ root: classes.headerText }} variant="h4">
                            Active Layers
                        </Typography>
                        <IconButton
                            classes={{ root: classes.moreButton }}
                            size="small"
                            onClick={handleMenu}>
                            <MoreVertIcon />
                        </IconButton>
                    </div>
                    <Menu
                        classes={{ paper: classes.menuPaper }}
                        transformOrigin={{
                            vertical: -44,
                            horizontal: 'center'
                        }}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}>
                        <MenuItem onClick={handleHideAllLayers}>Hide all layers</MenuItem>
                        <MenuItem onClick={handleRemoveAllLayers}>Remove all Layers</MenuItem>
                    </Menu>
                    <ScrollableContainer grow className={classes.scrollableContainer}>
                        <Legend />
                    </ScrollableContainer>
                </div>
            ) : null}
        </>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            width: 232,
            minHeight: 100,
            borderRadius: theme.constants.radius.sm,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.main,
            position: 'relative'
        },
        header: {
            textAlign: 'center'
        },
        headerText: {
            backgroundColor: theme.palette.background.dark,
            color: theme.palette.text.primary,
            textAlign: 'center',
            padding: theme.constants.spacing.md
        },
        menuPaper: {
            width: 232
        },
        moreButton: {
            position: 'absolute',
            right: 5,
            top: 5
        },
        scrollableContainer: {
            pointerEvents: 'all'
        }
    })
);

export default LegendControl;
